import i18next from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import ArrowIcon from "../../../assets/images/icons/arrow-down.svg";
import { Modal } from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { useFilter, useGetData } from "../../../hooks";
import RecordCardItem, {
  RecordCardType,
} from "../component/RecordCardItem/RecordCardItem";
import PatientMedicalRecordBody from "../PatientMedicalRecords/MedicalRecordDetails/PatientMedicalRecordBody";
import { MedicalKeys } from "../../../enums/MedicalReportEnums";
import { IStep, IStepFields } from "../../../interface/MedicalRecord";

const MedicalRecordInfo = () => {
  const { t } = useTranslation();
  const { recordId } = useParams();

  const { handleParamsAdd, handleParamsRemove } = useFilter();
  const GetMedicalRecord = useGetData<any>(
    EndPoints.GetMedicalRecord,
    {},
    recordId,
  );
  const { data, isLoading, isSuccess } = GetMedicalRecord;

  const [searchParams] = useSearchParams();

  const [openItemId, setOpenItemId] = useState<number>(data?.Data?.Visits?.[0]);

  const stepParam = searchParams.get("step");

  const TemplateId = data?.Data?.MedicalHistoryType?.Id;

  const { data: TemplateData, isLoading: isTemplateLoading } = useGetData<any>(
    EndPoints.MedicalHistoryTypes,
    {
      enabled: isSuccess,
    },
    TemplateId,
  );

  const isMedicalKey = (key?: string): key is keyof typeof MedicalKeys => {
    return `${key}` in MedicalKeys;
  };

  const stepNamesWithValues =
    data?.Data?.Attributes?.filter((step: any) =>
      step.StepFields?.some((field: IStepFields) => field.Value || field?.IsRepeatable),
    )?.map((step: any) => step.NameEn) || [];

  const steps = TemplateData?.Data?.Steps?.map((item: IStep) => {
    if (item?.InverseParentSteps && Array.isArray(item.InverseParentSteps)) {
      return {
        ...item,
        InverseParentSteps: item.InverseParentSteps.filter((parent: any) =>
          stepNamesWithValues.includes(parent?.NameEn),
        ),
      };
    }
    return item;
  }).filter((item: IStep) => {
    const hasMainStepValue = stepNamesWithValues.includes(item?.NameEn);
    const hasFilteredParentSteps = (item?.InverseParentSteps?.length ?? 0) > 0;

    const hasKeyStepsValues = (item?.StepFields ?? []).some((field) => {
      return isMedicalKey(field?.Key) && Boolean(data?.Data?.[field.Key]);
    });

    return hasMainStepValue || hasFilteredParentSteps || hasKeyStepsValues;
  });

  useEffect(() => {
    if (isSuccess) {
      setOpenItemId(data?.Data?.Visits?.[0]);
    }
  }, [isSuccess]);

  return (
    <>
      <div className="bg-primary-bg mt-4 flex-1 overflow-auto rounded-2xl p-4">
        <h2 className="mb-4 text-base font-bold">{t("last_medical_report")}</h2>
        <div className="flex flex-col">
          {isTemplateLoading || isLoading
            ? new Array(8)
                .fill(0)
                .map((_, index: number) => (
                  <div
                    key={index}
                    className="my-2 h-8 w-full animate-pulse select-none rounded-lg bg-gray-100"
                  />
                ))
            : steps?.map((step: any, index: number) => {
                return step?.InverseParentSteps?.length > 0 ? (
                  <div
                    key={step?.Id || index}
                    className="rounded-lg px-4 py-2.5 hover:bg-white"
                  >
                    <p className="">{step?.Name}</p>
                    <div className="flex flex-col flex-wrap">
                      {step?.InverseParentSteps?.filter(
                        (subStep: any) => !!subStep?.IsVisible,
                      )?.map((subStep: any, subIndex: number) => {
                        return (
                          <div
                            key={subStep?.Id || subIndex}
                            className="flex justify-between py-2 ps-2 cursor-pointer"
                            onClick={() =>
                              handleParamsAdd(`step`, `${subStep?.Id}`)
                            }
                          >
                            <p className="">{subStep?.Name}</p>
                            <img
                              alt="arrow"
                              src={ArrowIcon}
                              className={`${
                                i18next?.language === "ar"
                                  ? "rotate-90"
                                  : "-rotate-90"
                              } cursor-pointer`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div
                    key={step?.Id || index}
                    onClick={() => handleParamsAdd(`step`, `${step?.Id}`)}
                    className="flex cursor-pointer justify-between rounded-lg px-4 py-2.5 hover:bg-white"
                  >
                    <p className="">{step?.Name}</p>
                    <img
                      alt="arrow"
                      src={ArrowIcon}
                      className={`${
                        i18next?.language === "ar" ? "rotate-90" : "-rotate-90"
                      } cursor-pointer`}
                    />
                  </div>
                );
              })}
        </div>
      </div>
      <Modal
        customStyle="min-w-[90vw] min-h-[82vh]  relative"
        header={t("last_medical_report")}
        isOpen={!!stepParam}
        onClose={() => {
          handleParamsRemove("step");
        }}
      >
        <div className="flex gap-6 overflow-auto max-lg:flex-col">
          <div className="lg:w-[68%]">
            <PatientMedicalRecordBody
              TemplateData={TemplateData}
              GetMedicalRecord={GetMedicalRecord}
            />
          </div>
          <div className="flex flex-col items-center gap-2 lg:w-[32%]">
            {!isLoading &&
              data?.Data?.Visits?.map((e: any, i: number) => (
                <RecordCardItem
                  key={i}
                  item={e}
                  openItemId={openItemId}
                  setOpenItemId={setOpenItemId}
                  type={RecordCardType.FollowUp}
                />
              ))}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default MedicalRecordInfo;
